<template lang="pug">
v-expansion-panels#image-layer-editor(popout)
  name-editor(:layer="layer")

  dimension-editor(:dimensions="dimensions" :size="templateSize")

  v-expansion-panel
    v-expansion-panel-header Image Selection
    v-expansion-panel-content
      v-card
        v-card-text
          v-alert(type="info" outlined)
            span(align="center")
              = "Images can be uploaded here: "
              file-uploader(:folderId="gameImagesFolderId")
          v-radio-group(label="Selection Style:" v-model="imageNameStatic" row)
            v-radio(color="primary" label="Static" :value="true")
            v-radio(color="primary" label="Dynamic" :value="false")

          template(v-if="imageNameStatic")
            v-btn(@click="pickImageFromDrive") Pick Image...
            v-dialog(v-model="showImagePicker" max-width="640")
              v-card
                v-card-title Pick an Image
                v-card-subtitle
                  v-breadcrumbs.pa-0(:items="breadcrumbs" divider="/")
                    template(v-slot:item="{ item }")
                      v-breadcrumbs-item(:disabled="item.disabled" @click="!item.disabled && navigateTo(item.index)" style="cursor: pointer")
                        | {{ item.text }}
                v-card-text(style="min-height: 200px")
                  v-progress-circular(v-if="loadingImages" indeterminate color="primary")
                  template(v-else-if="isAtRoot")
                    v-list(v-if="allGameImageFolders.length" dense)
                      v-list-item(v-for="folder in allGameImageFolders" :key="folder.id" @click="navigateInto(folder)")
                        v-list-item-icon
                          v-icon mdi-folder-image
                        v-list-item-content
                          v-list-item-title {{ folder.name }}
                    p.grey--text(v-else) No image folders found.
                  template(v-else)
                    v-list(v-if="pickerFolders.length" dense class="mb-2")
                      v-list-item(v-for="folder in pickerFolders" :key="folder.id" @click="navigateInto(folder)")
                        v-list-item-icon
                          v-icon mdi-folder
                        v-list-item-content
                          v-list-item-title {{ folder.name }}
                    v-row(v-if="pickerImages.length" dense)
                      v-col(v-for="image in pickerImages" :key="image.id" cols="4")
                        v-card(@click="selectImage(image.id)" outlined hover style="cursor: pointer")
                          v-img(:src="thumbnailUrls[image.id]" aspect-ratio="1" contain)
                            template(v-slot:placeholder)
                              v-row.fill-height.ma-0(align="center" justify="center")
                                v-icon(large color="grey lighten-1") mdi-image
                          v-card-text.pa-1
                            .caption.text-truncate {{ image.name }}
                    p.grey--text(v-if="!pickerFolders.length && !pickerImages.length") No images found here. Upload images using the uploader above first.
                v-card-actions
                  v-spacer
                  v-btn(text @click="showImagePicker = false") Cancel

          template(v-else)
            v-text-field(v-model="imageNamePrefix" label="Prefix" dense outlined)
            v-select.image-name-property(v-model="imageNameProperty" :items="activeSheetProperties" dense outlined)
            v-text-field.image-name-suffix(v-model="imageNameSuffix" label="Suffix" dense outlined)
            v-text-field(disabled label="This Item" :value="dynamicImageName" dense outlined)

  v-expansion-panel
    v-expansion-panel-header Image Alignment
    v-expansion-panel-content
      v-card
        v-card-text
          v-layout
            v-flex(shrink)
              magic-property-input-talker(slot="prepend-inner" :layer="layer" attributeName="imageScaling")
            v-flex
              v-radio-group(label="Scaling:" v-model="imageScaling" row)
                v-radio(color="primary" label="Fit" value="fitToBox")
                v-radio(color="primary" label="Fill" value="fillToBox")
                v-radio(color="primary" label="Stretch" value="stretch")

          p Horizontal:
          magic-property-input-talker(:layer="layer" attributeName="horizontalAlignment")
          v-btn-toggle(v-model="horizontalAlignment")
            v-btn(small text value="left") Left
            v-btn(small text value="center") Center
            v-btn(small text value="right") Right

          p Vertical:
          magic-property-input-talker(:layer="layer" attributeName="verticalAlignment")
          v-btn-toggle(v-model="verticalAlignment")
            v-btn(small text value="top") Top
            v-btn(small text value="middle") Middle
            v-btn(small text value="bottom") Bottom

  v-expansion-panel
    v-expansion-panel-header Rotation
    v-expansion-panel-content
      v-card
        v-card-text
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="imageRotation"
              label="Rotation"
              min="0"
              max="360"
              step="1"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="rotationInput"
              dense
              outlined
              hide-details
              suffix="°"
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitRotation"
              @blur="commitRotation"
            )
          v-layout(wrap)
            v-btn(small text @click="imageRotation = 0") 0°
            v-btn(small text @click="imageRotation = 45") 45°
            v-btn(small text @click="imageRotation = 315") -45°
            v-btn(small text @click="imageRotation = 90") 90°
            v-btn(small text @click="imageRotation = 270") -90°
            v-btn(small text @click="imageRotation = 180") 180°

  v-expansion-panel
    v-expansion-panel-header Opacity
    v-expansion-panel-content
      v-card
        v-card-text
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="opacity"
              min="0"
              max="1"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.opacity"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('opacity', 0, 1, 2)"
              @blur="commitSliderInput('opacity', 0, 1, 2)"
            )
</template>

<script>
  import Vue from 'vue'
  import { mapActions, mapGetters } from 'vuex'
  import { debounce } from 'lodash'
  import { findProperty } from '../../../services/pdf_renderer/helpers'
  import { computedVModelUpdateAll } from '../../util/component_helper'
  import drive from '../../../services/google/drive'
  import { getCachedImage, setCachedImage } from '../../../services/image_cache'
  import NameEditor from './NameEditor.vue'
  import DimensionEditor from './DimensionEditor.vue'
  import MagicPropertyInputTalker from '../../source/MagicPropertyInputTalker.vue'
  import FileUploader from '../../shared/FileUploader.vue'

  export default {
    props: ["layer"],

    data() {
      const l = this.layer
      return {
        showImagePicker: false,
        loadingImages: false,
        pickerImages: [],
        pickerFolders: [],
        folderStack: [],
        thumbnailUrls: {},
        rotationInput: String(l.imageRotation ?? 0),
        sliderInputs: {
          opacity: String(l.opacity ?? 1),
        }
      }
    },

    components: {
      NameEditor,
      DimensionEditor,
      MagicPropertyInputTalker,
      FileUploader
    },

    computed: {
      ...mapGetters([
        "workingDirectoryId",
        "getLayerDimensions",
        "findTemplateByLayerId",
        "gameImagesFolderOrDefault",
        "gameImagesFolder",
        "gameFolderOrDefault",
        "allGames",
        "activeItem",
        "activeSheetProperties",
        "activeComponent",
        "activeGame",
      ]),

      gameImagesFolderId() {
        return this.gameImagesFolderOrDefault(this.activeGame).id
      },

      dimensions() { return this.getLayerDimensions(this.layer) },

      templateSize() {
        const template = this.findTemplateByLayerId(this.layer.id)
        return template && template.size
      },

      ...computedVModelUpdateAll("layer", "patchLayer", [
        "imageNameStatic",
        "imageId",
        "imageNamePrefix",
        "imageNameProperty",
        "imageNameSuffix",
        "imageScaling",
        "horizontalAlignment",
        "verticalAlignment",
        "imageRotation",
        "opacity",
      ]),

      isAtRoot() {
        return this.folderStack.length === 0
      },

      allGameImageFolders() {
        return this.allGames.reduce((acc, game) => {
          const imagesFolder = this.gameImagesFolder(game)
          if(!imagesFolder) { return acc }
          const gameFolder = this.gameFolderOrDefault(game)
          acc.push({ id: imagesFolder.id, name: gameFolder.name })
          return acc
        }, [])
      },

      breadcrumbs() {
        const root = { text: 'Images', index: -1, disabled: this.isAtRoot }
        const rest = this.folderStack.map((folder, index) => ({
          text: folder.name,
          index,
          disabled: index === this.folderStack.length - 1,
        }))
        return [root, ...rest]
      },

      dynamicImageName() {
        const
          prefix = this.layer.imageNamePrefix,
          property = findProperty(this.activeItem, this.layer.imageNameProperty),
          suffix = this.layer.imageNameSuffix

        return `${prefix}${property}${suffix}`
      }
    },

    methods: {
      ...mapActions(["patchLayer"]),

      getImageLabel(imageItem) { return imageItem },

      pickImageFromDrive() {
        this.folderStack = []
        this.pickerFolders = []
        this.pickerImages = []
        this.showImagePicker = true
      },

      loadFolder(folderId) {
        this.loadingImages = true
        this.pickerImages = []
        this.pickerFolders = []
        drive.getIndex([folderId], {})
          .then(({ folders, images }) => {
            this.pickerFolders = folders
            this.pickerImages = images
            this.loadThumbnails(images)
          })
          .finally(() => {
            this.loadingImages = false
          })
      },

      loadThumbnails(images) {
        images.forEach((image) => {
          getCachedImage(image.md5).then((cached) => {
            if(cached) {
              this.$set(this.thumbnailUrls, image.id, cached)
            } else {
              this.$store.dispatch('googleDownloadFile', image.id).then((data) => {
                const dataUrl = `data:${image.mimeType};base64,${btoa(data)}`
                setCachedImage(image.md5, dataUrl)
                this.$set(this.thumbnailUrls, image.id, dataUrl)
              })
            }
          })
        })
      },

      navigateInto(folder) {
        this.folderStack.push(folder)
        this.loadFolder(folder.id)
      },

      navigateTo(index) {
        if(index === -1) {
          this.folderStack = []
          this.pickerFolders = []
          this.pickerImages = []
        } else {
          this.folderStack = this.folderStack.slice(0, index + 1)
          this.loadFolder(this.folderStack[index].id)
        }
      },

      selectImage(imageId) {
        this.imageId = imageId
        this.showImagePicker = false
      },

      commitRotation() {
        const parsed = parseFloat(this.rotationInput)
        if (!isNaN(parsed)) {
          const normalized = ((Math.round(parsed) % 360) + 360) % 360
          this.imageRotation = normalized
          this.rotationInput = String(normalized)
        } else {
          this.rotationInput = String(this.imageRotation)
        }
      },

      commitSliderInput(prop, min, max, decimals) {
        const parsed = parseFloat(this.sliderInputs[prop])
        if (!isNaN(parsed)) {
          const clamped = Math.min(max, Math.max(min, parsed))
          const rounded = parseFloat(clamped.toFixed(decimals))
          this[prop] = rounded
          this.sliderInputs[prop] = String(rounded)
        } else {
          this.sliderInputs[prop] = String(this[prop])
        }
      },
    },

    watch: {
      imageRotation(val) { this.rotationInput = String(val) },
      opacity(val)       { this.sliderInputs.opacity = String(val) },
    }
  }
</script>
